import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { getSummary, getStores, getRegions, getStoreDetail } from '../api/suApi';
import type { CommonParams, GroupBy, Period, StoresResponse, SummaryResponse, RegionsResponse, StoreDetailResponse } from '../api/types';
import { selectFilters, selectOrg, Org, SuFiltersState } from '../state/filtersSlice';
import { useAsync, AsyncState } from './useAsync';

const toCommon = (f: SuFiltersState): CommonParams => ({
  period: f.period,
  store_type: f.storeType === 'all' ? undefined : f.storeType,
  region: f.regions.length ? f.regions.join(',') : undefined,
  city: f.cities.length ? f.cities.join(',') : undefined,
});

const useOrgValue = () => useSelector((s: any) => selectOrg(s)) as Org;

function useCommonParams(): CommonParams {
  const f = useSelector((s: any) => selectFilters(s)) as SuFiltersState;
  return useMemo(() => toCommon(f), [f]);
}

/** `period` overrides the selected period (Home asks for yesterday next to today); `enabled=false` skips the request. */
export function useSummary(period?: Period, enabled = true): AsyncState<SummaryResponse> {
  const common = useCommonParams();
  const org = useOrgValue();
  const params = useMemo(() => (period ? { ...common, period } : common), [common, period]);
  return useAsync((signal) => getSummary(params, { signal, org }), `${org}:${JSON.stringify(params)}`, enabled);
}

export interface UseStoresOpts {
  limit?: number;
  offset?: number;
}

export function useStores(opts: UseStoresOpts = {}): AsyncState<StoresResponse> {
  const f = useSelector((s: any) => selectFilters(s)) as SuFiltersState;
  const org = useOrgValue();
  const params = useMemo(
    () => ({ ...toCommon(f), sort: f.sort.key, order: f.sort.order, limit: opts.limit, offset: opts.offset }),
    [f, opts.limit, opts.offset],
  );
  return useAsync((signal) => getStores(params, { signal, org }), `${org}:${JSON.stringify(params)}`);
}

/** groupBy defaults to 'state'. Pass 'city' for the city breakdown. */
export function useRegions(groupBy?: GroupBy): AsyncState<RegionsResponse> {
  const f = useSelector((s: any) => selectFilters(s)) as SuFiltersState;
  const org = useOrgValue();
  const params = useMemo(
    () => ({ ...toCommon(f), group_by: groupBy ?? ('state' as const) }),
    [f, groupBy],
  );
  return useAsync((signal) => getRegions(params, { signal, org }), `${org}:${JSON.stringify(params)}`);
}

export function useStoreDetail(siteCode: string | undefined): AsyncState<StoreDetailResponse> {
  const org = useOrgValue();
  return useAsync((signal) => getStoreDetail(siteCode as string, { signal, org }), `detail:${org}:${siteCode}`, !!siteCode);
}
